import { horasTrabajadasEfectivas } from './horasTareoSimple';

function ymd(valor) {
  if (!valor) return null;
  const s = String(valor);
  if (s.includes('T')) return s.split('T')[0];
  if (s.includes(' ')) return s.split(' ')[0];
  return s;
}

function compactarHistorial(regs) {
  if (!Array.isArray(regs)) return [];
  return regs
    .filter((h) => h && (h.fecha_ingreso || h.fechaIngreso))
    .map((h) => ({
      id: h.id || null,
      fecha_ingreso: ymd(h.fecha_ingreso || h.fechaIngreso),
      fecha_cese: ymd(h.fecha_cese || h.fechaCese),
      motivo_cese: h.motivo_cese || h.motivoCese || null,
    }));
}

function compactarConcepto(c, index = 0) {
  if (!c || typeof c !== 'object') return null;
  const codigo = String(
    c.codigoPlame
    || c.codigo_plame
    || c.codigo
    || c.plame_configuracion?.codigo_plame
    || c.id_concepto?.codigo_plame
    || ''
  );
  const monto = Number(
    c.monto ?? c.valor ?? c.monto_interna ?? c.monto_calculado ?? 0
  );
  return {
    id: c.id || `snap_${codigo || index}`,
    codigo,
    codigo_plame: codigo,
    codigoPlame: codigo,
    descripcion: c.descripcion || c.nombre || c.concepto || codigo,
    monto,
    valor: monto,
    tipo_concepto: c.tipo_concepto || c.tipoConcepto || null,
    base_calculo: Number(c.base_calculo || c.base_imponible || 0),
    destino_ajuste: c.destino_ajuste || c.destinoAjuste || null,
    excluirBoletaOrdinaria: Boolean(c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria),
    excluir_boleta_ordinaria: Boolean(c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria),
    origenCalculo: c.origenCalculo || c.origen_calculo || c.origen || null,
    origen_calculo: c.origenCalculo || c.origen_calculo || c.origen || null,
  };
}

function compactarGrupoConceptos(grupo) {
  if (!grupo || typeof grupo !== 'object') return { ingresos: [], descuentos: [], aportes: [] };
  const mapArr = (arr) => (Array.isArray(arr) ? arr.map(compactarConcepto).filter(Boolean) : []);
  return {
    ingresos: mapArr(grupo.ingresos || grupo.dataIngresos),
    descuentos: mapArr(grupo.descuentos || grupo.dataDescuentos),
    aportes: mapArr(grupo.aportes || grupo.dataAportes),
  };
}

function compactarConfigTareo(cfg) {
  if (!cfg || typeof cfg !== 'object') return null;
  return {
    tipo_tareo: cfg.tipo_tareo ?? null,
    modo_horas_simple: cfg.modo_horas_simple || 'reales',
    es_simple: Boolean(cfg.es_simple || Number(cfg.tipo_tareo) === 1),
    es_detallado: Boolean(cfg.es_detallado),
    descontar_descansos: Boolean(cfg.descontar_descansos),
  };
}

function esConceptoViaticoSnap(c) {
  const codigo = String(c?.codigo || c?.codigo_plame || c?.codigoPlame || '').trim();
  if (codigo === '0917') return true;
  const desc = String(c?.descripcion || c?.nombre || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return desc.includes('condiciones de trabajo') || desc.includes('viatico');
}

function viaticoInternoDesdeTareos(tareos) {
  let monto = 0;
  let descripcion = 'CONDICIONES DE TRABAJO';
  (tareos || []).forEach((t) => {
    (t.conceptos || []).forEach((c) => {
      if (!esConceptoViaticoSnap(c)) return;
      const tb = String(c.tipo_boleta || 'plame').toLowerCase();
      if (tb === 'plame') return;
      const v = Number(c.monto) || 0;
      if (v <= 0) return;
      monto += v;
      if (c.descripcion) descripcion = c.descripcion;
    });
  });
  if (monto <= 0) return null;
  return {
    id: 'snap_0917',
    codigo: '0917',
    codigo_plame: '0917',
    codigoPlame: '0917',
    descripcion,
    monto,
    valor: monto,
    tipo_concepto: 'ingreso',
    origenCalculo: 'viatico',
    origen_calculo: 'viatico',
    excluirBoletaOrdinaria: true,
    excluir_boleta_ordinaria: true,
  };
}

function asegurarViaticoInternoEnGrupo(grupo, tareos) {
  const base = grupo && typeof grupo === 'object'
    ? grupo
    : { ingresos: [], descuentos: [], aportes: [] };
  const ingresos = [...(base.ingresos || [])];
  const viaTareo = viaticoInternoDesdeTareos(tareos);
  const idx = ingresos.findIndex(esConceptoViaticoSnap);
  if (idx >= 0) {
    ingresos[idx] = {
      ...ingresos[idx],
      origenCalculo: ingresos[idx].origenCalculo || ingresos[idx].origen_calculo || 'viatico',
      origen_calculo: ingresos[idx].origen_calculo || ingresos[idx].origenCalculo || 'viatico',
      excluirBoletaOrdinaria: true,
      excluir_boleta_ordinaria: true,
      monto: Math.max(Number(ingresos[idx].monto) || 0, viaTareo?.monto || 0),
    };
    ingresos[idx].valor = ingresos[idx].monto;
  } else if (viaTareo) {
    ingresos.push(viaTareo);
  }
  return { ...base, ingresos };
}

function compactarTareos(tareos, configuracionTareo = null) {
  if (!Array.isArray(tareos)) return [];
  return tareos.map((t) => {
    if (!t || typeof t !== 'object') return null;
    const conceptos = Array.isArray(t.conceptos)
      ? t.conceptos.map((c) => ({
          tipo_boleta: c.tipo_boleta || 'plame',
          tipo_concepto: c.tipo_concepto || null,
          codigo: c.plame_configuracion?.codigo_plame || c.codigo_plame || c.codigo || null,
          descripcion: c.plame_configuracion?.descripcion || c.descripcion || null,
          monto: Number(c.monto) || 0,
          plame_configuracion_id: c.plame_configuracion_id || c.plame_configuracion?.id || null,
        }))
      : [];
    const horasReales = Number(t.horas_trabajadas || t.horasTrabajadas || 0);
    return {
      fecha: ymd(t.fecha),
      tipo_evento: t.tipo_evento || t.tipoEvento || null,
      horas_trabajadas: horasReales,
      horas_planilla: Number(
        t.horas_planilla != null
          ? t.horas_planilla
          : horasTrabajadasEfectivas(t, configuracionTareo)
      ),
      hora_entrada: t.hora_entrada || null,
      hora_salida: t.hora_salida || null,
      conceptos,
    };
  }).filter((t) => t && t.fecha);
}

function compactarBoleta(boleta) {
  const data = boleta?.data || boleta;
  if (!data || typeof data !== 'object') return null;
  const grupo = compactarGrupoConceptos({
    ingresos: data.dataIngresos,
    descuentos: data.dataDescuentos,
    aportes: data.dataAportes,
  });
  return {
    success: true,
    data: {
      dataIngresos: grupo.ingresos,
      dataDescuentos: grupo.descuentos,
      dataAportes: grupo.aportes,
      totales: data.totales || null,
      dias_tramo: data.dias_tramo ?? boleta?.dias_tramo ?? null,
    },
    dias_tramo: boleta?.dias_tramo ?? data.dias_tramo ?? null,
    sueldo_partido: boleta?.sueldo_partido || null,
  };
}

export function armarSnapshotInternaCierre(emp) {
  if (!emp || typeof emp !== 'object') return null;
  const tipo = String(emp.tipo_planilla || emp.datosSalariales?.tipoPlanilla || '').toLowerCase();
  const sep = emp.conceptos_separados_internos || emp.conceptosInternos || null;
  const tieneInterna = tipo === 'dual' || tipo === 'solo_interna'
    || Number(emp.sueldo_interno || emp.datosSalariales?.sueldoInterno || 0) > 0
    || Boolean(emp.boleta_interna_calculada)
    || Boolean(sep && ((sep.ingresos || []).length + (sep.descuentos || []).length + (sep.aportes || []).length));

  if (!tieneInterna && !(emp.fecha_ingreso_interna || emp.historial_laboral_interno?.length)) {
    return null;
  }

  const grupoBase = compactarGrupoConceptos(sep || {});
  const cfgTareo = compactarConfigTareo(
    emp.configuracion_tareo || emp.configuracionTareo || null
  );
  const tareosPlame = compactarTareos(emp.tareosEmpleado, cfgTareo);
  const tareosInterna = compactarTareos(
    emp.tareosEmpleadoInterna?.length ? emp.tareosEmpleadoInterna : emp.tareosEmpleado,
    cfgTareo
  );
  const grupo = asegurarViaticoInternoEnGrupo(
    grupoBase,
    [...tareosInterna, ...tareosPlame]
  );
  const boletaInterna = compactarBoleta(emp.boleta_interna_calculada);
  const grupoDesdeBoleta = boletaInterna?.data
    ? compactarGrupoConceptos({
        ingresos: boletaInterna.data.dataIngresos,
        descuentos: boletaInterna.data.dataDescuentos,
        aportes: boletaInterna.data.dataAportes,
      })
    : null;
  const grupoFinal = grupo.ingresos.length
    ? grupo
    : asegurarViaticoInternoEnGrupo(grupoDesdeBoleta || grupo, tareosInterna);
  const totalesDesdeGrupo = (g) => {
    const ti = (g.ingresos || []).reduce((s, c) => (
      c.excluirBoletaOrdinaria || c.excluir_boleta_ordinaria ? s : s + Number(c.monto || 0)
    ), 0);
    const td = (g.descuentos || []).reduce((s, c) => s + Number(c.monto || 0), 0);
    const ta = (g.aportes || []).reduce((s, c) => s + Number(c.monto || 0), 0);
    return { totalIngresos: ti, totalDescuentos: td, totalAportes: ta, netoAPagar: ti - td };
  };
  return {
    tipo_planilla: emp.tipo_planilla || emp.datosSalariales?.tipoPlanilla || null,
    sueldo_interno: emp.sueldo_interno ?? emp.datosSalariales?.sueldoInterno ?? null,
    fecha_ingreso_interna: ymd(emp.fecha_ingreso_interna || emp.fechaIngresoInterna),
    historial_laboral: compactarHistorial(emp.historial_laboral || emp.historialLaboral),
    historial_laboral_interno: compactarHistorial(
      emp.historial_laboral_interno || emp.historialLaboralInterno
    ),
    omitir_descuentos_plame_interna: Boolean(
      emp.omitir_descuentos_plame_interna
      || emp.datosSalariales?.omitirDescuentosPlameInterna
    ),
    boleta_interna_partida: Boolean(emp.boleta_interna_partida),
    sueldo_partido: emp.sueldo_partido || null,
    dias_interna_1: emp.dias_interna_1 ?? null,
    dias_interna_2: emp.dias_interna_2 ?? null,
    sueldo_interno_1: emp.sueldo_interno_1 ?? emp.sueldoInterno1 ?? null,
    sueldo_interno_2: emp.sueldo_interno_2 ?? emp.sueldoInterno2 ?? null,
    conceptos_separados_internos: grupoFinal,
    conceptos_separados_internos_1: emp.conceptos_separados_internos_1
      ? compactarGrupoConceptos(emp.conceptos_separados_internos_1)
      : null,
    conceptos_separados_internos_2: emp.conceptos_separados_internos_2
      ? compactarGrupoConceptos(emp.conceptos_separados_internos_2)
      : null,
    boleta_interna_calculada: boletaInterna,
    tareosEmpleado: tareosPlame,
    tareosEmpleadoInterna: tareosInterna,
    configuracion_tareo: cfgTareo,
    monto_viatico_standard: emp.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? emp.datosSalariales?.montoViaticoStandard
      ?? null,
    totales_internos: emp.totalesInternos || emp.totales_internos || totalesDesdeGrupo(grupoFinal),
  };
}

export function aplicarSnapshotInternaCierre(emp, snap) {
  const fuente = snap && typeof snap === 'object' ? snap : (emp?.snapshot_interna || null);
  if (!fuente || typeof fuente !== 'object') return emp || {};

  const sep = asegurarViaticoInternoEnGrupo(
    fuente.conceptos_separados_internos || compactarGrupoConceptos({}),
    [
      ...(fuente.tareosEmpleadoInterna || []),
      ...(fuente.tareosEmpleado || []),
    ]
  );
  const internList = [
    ...(sep.ingresos || []),
    ...(sep.descuentos || []),
    ...(sep.aportes || []),
  ];

  return {
    ...emp,
    tipo_planilla: fuente.tipo_planilla ?? emp.tipo_planilla,
    sueldo_interno: fuente.sueldo_interno ?? emp.sueldo_interno,
    fecha_ingreso_interna: fuente.fecha_ingreso_interna ?? emp.fecha_ingreso_interna,
    historial_laboral: (fuente.historial_laboral?.length
      ? fuente.historial_laboral
      : emp.historial_laboral) || [],
    historial_laboral_interno: (fuente.historial_laboral_interno?.length
      ? fuente.historial_laboral_interno
      : emp.historial_laboral_interno) || [],
    omitir_descuentos_plame_interna: Boolean(fuente.omitir_descuentos_plame_interna),
    boleta_interna_partida: Boolean(fuente.boleta_interna_partida),
    sueldo_partido: fuente.sueldo_partido ?? emp.sueldo_partido ?? null,
    dias_interna_1: fuente.dias_interna_1 ?? emp.dias_interna_1 ?? null,
    dias_interna_2: fuente.dias_interna_2 ?? emp.dias_interna_2 ?? null,
    sueldo_interno_1: fuente.sueldo_interno_1 ?? emp.sueldo_interno_1 ?? null,
    sueldo_interno_2: fuente.sueldo_interno_2 ?? emp.sueldo_interno_2 ?? null,
    sueldoInterno1: fuente.sueldo_interno_1 ?? emp.sueldoInterno1 ?? null,
    sueldoInterno2: fuente.sueldo_interno_2 ?? emp.sueldoInterno2 ?? null,
    conceptos_separados_internos: sep,
    conceptos_internos: internList,
    conceptosInternos: sep,
    conceptos_separados_internos_1: fuente.conceptos_separados_internos_1 || null,
    conceptos_separados_internos_2: fuente.conceptos_separados_internos_2 || null,
    conceptosInternos1: fuente.conceptos_separados_internos_1 || emp.conceptosInternos1 || null,
    conceptosInternos2: fuente.conceptos_separados_internos_2 || emp.conceptosInternos2 || null,
    boleta_interna_calculada: fuente.boleta_interna_calculada || emp.boleta_interna_calculada || null,
    tareosEmpleado: (fuente.tareosEmpleado?.length ? fuente.tareosEmpleado : emp.tareosEmpleado) || [],
    tareosEmpleadoInterna: (fuente.tareosEmpleadoInterna?.length
      ? fuente.tareosEmpleadoInterna
      : emp.tareosEmpleadoInterna) || [],
    configuracion_tareo: fuente.configuracion_tareo || emp.configuracion_tareo || null,
    monto_viatico_standard: fuente.monto_viatico_standard
      ?? emp.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? null,
    montoViaticoStandard: fuente.monto_viatico_standard
      ?? emp.montoViaticoStandard
      ?? emp.monto_viatico_standard
      ?? null,
    totalesInternos: fuente.totales_internos || emp.totalesInternos || null,
  };
}
